import { Auth } from 'aws-amplify';

import { STUDIO_REFERRER } from 'src/routes/onboarding/setup/constants';
import { getAuthRequestInfo, GetSignInMetadata } from 'src/utils/AuthUtils';
import { ensureError } from 'src/utils/Errors';
import { removeSpaceToLowerCase } from 'src/utils/StringUtils';
import { generatePassword } from 'src/utils/UserUtils';

export const STUDIO_ACCOUNT_ALREADY_EXISTS_CODE =
  'StudioAccountAlreadyExistsException';

// Placeholder names satisfy Cognito's required attributes until workspace setup
// collects the user's real name; treated as "no real name" when prefilling.
export const STUDIO_PLACEHOLDER_GIVEN_NAME = 'Studio';
export const STUDIO_PLACEHOLDER_FAMILY_NAME = 'User';

// Cognito's signUp API requires a password even when users never set one.
// Studio onboarding is passwordless from the user's perspective: we generate
// a random password here and never surface it in the UI.
const STUDIO_SIGNUP_PASSWORD_LENGTH = 16;

const getStudioClientMetadata = async () => {
  const requestInfo = await getAuthRequestInfo();
  return {
    ...GetSignInMetadata(undefined, requestInfo),
    referrer: STUDIO_REFERRER,
  };
};

const getCognitoErrorCode = (error: unknown): string | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }

  return ensureError(error).name;
};

const isAlreadyConfirmedSignupUser = (error: unknown) => {
  const errorCode = getCognitoErrorCode(error);
  const message = ensureError(error).message.toLowerCase();

  return (
    errorCode === 'InvalidParameterException' &&
    message.includes('already confirmed')
  );
};

const createStudioAccountAlreadyExistsError = () =>
  Object.assign(new Error(STUDIO_ACCOUNT_ALREADY_EXISTS_CODE), {
    code: STUDIO_ACCOUNT_ALREADY_EXISTS_CODE,
  });

export const getStudioVerificationErrorMessage = (error: unknown) => {
  const errorCode = getCognitoErrorCode(error);

  if (errorCode === 'CodeMismatchException') {
    return 'Incorrect code — try again.';
  }
  if (errorCode === 'ExpiredCodeException') {
    return 'This code has expired. Resend a new code and try again.';
  }

  return (
    ensureError(error).message || 'Unable to verify code. Please try again.'
  );
};

export const studioSignUpWithEmail = async (email: string) => {
  const normalizedEmail = removeSpaceToLowerCase(email);
  const clientMetadata = await getStudioClientMetadata();
  const password = generatePassword(STUDIO_SIGNUP_PASSWORD_LENGTH);
  try {
    const signUpResult = await Auth.signUp({
      username: normalizedEmail,
      password,
      attributes: {
        email: normalizedEmail,
        given_name: STUDIO_PLACEHOLDER_GIVEN_NAME,
        family_name: STUDIO_PLACEHOLDER_FAMILY_NAME,
      },
      clientMetadata,
    });

    return {
      username: normalizedEmail,
      userConfirmed: signUpResult.userConfirmed,
      password,
    };
  } catch (error) {
    if (getCognitoErrorCode(error) === 'UsernameExistsException') {
      try {
        await Auth.resendSignUp(normalizedEmail, clientMetadata);
      } catch (resendError) {
        if (isAlreadyConfirmedSignupUser(resendError)) {
          throw createStudioAccountAlreadyExistsError();
        }
        throw resendError;
      }
      return {
        username: normalizedEmail,
        userConfirmed: false,
        password,
      };
    }
    throw error;
  }
};

export const confirmStudioEmailVerification = async (
  username: string,
  code: string,
) => {
  await Auth.confirmSignUp(username, code);
};

export const resendStudioEmailVerificationCode = async (username: string) => {
  const clientMetadata = await getStudioClientMetadata();
  await Auth.resendSignUp(username, clientMetadata);
};
